import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import {
    Field, Form, Formik, FormikProps,
} from 'formik';

import { Brief, userAtom } from '@/components/with_auth/State';
import { UPDATE_BRIEF, UPDATE_USER, updateBriefDirect } from '@/components/onboarding/Actions';
import {
    haveErrors,
    reportGeneralError,
    BIO_NAMES,
    BIO_STATES,
} from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals';
import FormError from '@/components/forms/FormError';
import useIsEdit from '@/hooks/use_is_edit';

import bioStyles from './Bio.module.css';
import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import TextAreaInput from './TextArea';

function EditBioPanel({
    isModal,
    extendContinue,
    overrideBack,
    isEdit,
    openBrief,

}: bioPageProps) {
    const router = useRouter();
    const formRef = useRef<FormikProps<any>>(null);
    const [user, setUser] = useRecoilState(userAtom);

    const [isLoading, setIsLoading] = useState(false);
    const { url } = useIsEdit();

    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);

    const [brief, setBrief] = useState<Brief>(openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex)
        : user.briefs[0]);

    useEffect(() => {
        const currentBrief = openBrief
            ? user.briefs.find((item) => item.id === openBrief.briefIndex)
            : user.briefs[0];

        if (currentBrief) {
            setBrief(currentBrief);
        }
    }, [openBrief, user]);

    const handleContinue = async () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleBackNavigation = () => {
        let backRoute = routes.BIO_REVISE_BIO;
        // If the bio was skipped
        if (brief && brief.skippedBioGeneration) {
            // remove the skip / clear previously selected bio
            updateBriefDirect(brief.id, { skippedBioGeneration: false, biography: '' }, { user, setUser });
            // take us back to before bio generation
            backRoute = routes.BIO_SELECT_IMPORT_METHOD;
        }
        if (isModal && overrideBack) {
            overrideBack(true);
        } else {
            router.push(backRoute);
        }
    };

    const handleBriefUpdate = async (attributes, setSubmitting, setStatus) => {
        setIsLoading(true);
        updateBrief({
            variables: {
                input: {
                    id: brief.id, // initial brief
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, async (data) => {
                setSubmitting(false);
                const { errors } = data.updateBrief;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'brief');
                    return;
                }

                setStatus({ errors: {} });
                setIsLoading(false);

                // Save the bio onto the user so it's stored in recoil
                const newBrief = data.updateBrief?.brief;
                if (newBrief) {
                    const newUser = { ...user };
                    newUser.briefs = newUser.briefs.map((item) => {
                        if (item.id === newBrief.id) {
                            return newBrief;
                        }
                        return item;
                    });
                    setUser(newUser);
                }

                if (url) {
                    router.push(url);
                } else if (isModal && extendContinue) {
                    extendContinue('', isEdit);
                } else {
                    router.push(routes.BIO_COMMUNICATION_PREFERENCES);
                }
            }),
        });
    };

    const handleUserUpdate = async (attributes, setSubmitting, setStatus) => {
        setIsLoading(true);
        updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, async (response) => {
                setSubmitting(false);
                const { errors } = response.updateUser;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'user');
                    return;
                }

                setUser(response.updateUser.user);
                setStatus({ errors: {} });
                setIsLoading(false);
                // if (url) {
                //     router.push(url);
                // } else if (isModal && extendContinue) {
                //     extendContinue();
                // } else {
                //     router.push(routes.BIO_COMMUNICATION_PREFERENCES);
                // }
            }),
        });
    };

    const placeholder = 'Ryan Cornell-Hayes is the senior engineering manager at Amity Software. For sixteen years, they have worked on successful software teams, emphasizing diversity and equality both in product and team dynamics. In their spare time, Ryan plays pickleball, collects vintage bottlecaps, and mentors underprivileged youths.';
    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8 sm:pb-0" step={2} totalSteps={5} isModal={isModal}>
            <div className="w-full py-2 pb-0 px-2 sm:px-5">
                <BioPageHeader
                    header="Finally, let&apos;s put it in your own words."
                    subHeader="Take one last look before you publish. This is your chance to rewrite, revise, add or remove anything you'd like. Give it the personal touch only you can provide."
                    subHeaderWidth="max-w-[650px]"
                    subHeaderPadding="pb-4"
                />
                <div className="rounded-lg border border-gray-200 bg-cirrusWhite shadow-md flex flex-col">
                    <div className="p-2 sm:p-8 w-full justify-between">

                        <div className="">
                            <div className="resize-none min-h-[319px] text-cleanSlate font-instrument text-base font-normal leading-7 ">
                                <Formik
                                    innerRef={formRef}
                                    onSubmit={async (frm, { setStatus, setSubmitting }) => {
                                        if (!frm.biography) {
                                            setStatus({ errors: { biography: ['Please enter a biography.'] } });
                                            setSubmitting(false);
                                            return;
                                        }

                                        const attributes = {
                                            // onboardingState: BIO_STATES.meetingPreferences,
                                            biography: frm.biography,
                                        };

                                        await handleBriefUpdate(
                                            attributes,
                                            setSubmitting,
                                            setStatus,
                                        );
                                        await handleUserUpdate(
                                            { onboardingState: BIO_STATES.meetingPreferences },
                                            setSubmitting,
                                            setStatus,
                                        );
                                    }}
                                    initialValues={{
                                        biography: brief.biography || '',
                                    }}
                                >
                                    {({ status, setStatus, handleChange }) => (
                                        <Form>
                                            <Field
                                                id="biography"
                                                name="biography"
                                                placeholder={placeholder}
                                                component={TextAreaInput}
                                                onChange={(e) => {
                                                    if (status?.errors?.biography) {
                                                        setStatus({ errors: {} });
                                                    }
                                                    handleChange(e);
                                                }}
                                                className="resize-none min-h-[508px] text-cleanSlate font-instrument text-base font-normal leading-7 px-4 py-3 w-full"
                                            />
                                            <FormError
                                                error={status?.errors?.biography as string[]}
                                            />
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="f-full">
                    <NavButtons
                        containerStyles={bioStyles.buttonGroupWithoutBio}
                        handleBackNavigation={handleBackNavigation}
                        handleContinue={handleContinue}
                        isLoading={isLoading}
                        isEdit={url || isEdit}
                    />
                </div>
            </div>

        </PanelContainer>
    );
}

export default EditBioPanel;
